import React, { useState, useEffect } from 'react';
import { FiFileText, FiDownload, FiTool, FiDollarSign, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { MantenimientoService } from '../services/mantenimientoService';
import { AutosService } from '../services/autosService';
import './ReporteMantenimiento.css';

const ReporteMantenimiento = () => {
  const [mantenimientos, setMantenimientos] = useState([]);
  const [autos, setAutos] = useState([]);
  const [filtros, setFiltros] = useState({
    autoId: '',
    fechaInicio: '',
    fechaFin: '',
    tipoMantenimiento: ''
  });

  useEffect(() => {
    const unsubscribeMantenimientos = MantenimientoService.suscribirseMantenimientos(setMantenimientos);
    const unsubscribeAutos = AutosService.suscribirseAutos(setAutos);

    return () => {
      if (typeof unsubscribeMantenimientos === 'function') unsubscribeMantenimientos();
      if (typeof unsubscribeAutos === 'function') unsubscribeAutos();
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const getAutoNombre = (autoId) => {
    const auto = autos.find(a => a.id === autoId);
    if (!auto) return 'No especificado';
    
    if (auto.placas) return `${auto.placas} - ${auto.marca || ''} ${auto.modelo || ''}`;
    if (auto.nombreProveedor) return auto.nombreProveedor;
    if (auto.nombreEmpleado) return auto.nombreEmpleado;
    return 'Sin identificación';
  };

  const mantenimientosFiltrados = mantenimientos.filter(mantenimiento => {
    if (filtros.autoId && mantenimiento.autoId !== filtros.autoId) return false;
    if (filtros.fechaInicio && mantenimiento.fecha < filtros.fechaInicio) return false;
    if (filtros.fechaFin && mantenimiento.fecha > filtros.fechaFin) return false;
    if (filtros.tipoMantenimiento && mantenimiento.tipoMantenimiento !== filtros.tipoMantenimiento) return false;
    return true;
  });

  const calcularEstadisticas = () => {
    const totalMantenimientos = mantenimientosFiltrados.length;
    const montoTotal = mantenimientosFiltrados.reduce((sum, m) => sum + (parseFloat(m.monto) || 0), 0);
    const mantenimientosPreventivos = mantenimientosFiltrados.filter(m => m.tipoMantenimiento === 'preventivo').length;
    const mantenimientosCorrectivos = mantenimientosFiltrados.filter(m => m.tipoMantenimiento === 'correctivo').length;
    const mantenimientosEmergencia = mantenimientosFiltrados.filter(m => m.tipoMantenimiento === 'emergencia').length;

    return {
      totalMantenimientos,
      montoTotal,
      mantenimientosPreventivos,
      mantenimientosCorrectivos,
      mantenimientosEmergencia
    };
  };

  const exportarAExcel = () => {
    const estadisticas = calcularEstadisticas();
    const fechaActual = new Date().toLocaleDateString('es-MX', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    let htmlContent = `
      <html xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head>
        <meta charset="UTF-8">
        <style>
          table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; }
          td, th { border: 1px solid #000; padding: 8px; text-align: center; }
          .titulo { background-color: #4472C4; color: white; font-size: 16px; font-weight: bold; }
          .fecha { font-style: italic; font-size: 11px; text-align: left; }
          .header-morado { background-color: #7030A0; color: white; font-weight: bold; font-size: 13px; }
          .header-azul { background-color: #00B0F0; color: white; font-weight: bold; font-size: 12px; }
          .total-azul { background-color: #00B0F0; color: white; font-weight: bold; border-top: 2px solid #000; border-bottom: 2px solid #000; }
          .dato { background-color: #FFFFFF; }
          .dato-alternado { background-color: #F2F2F2; }
          .text-left { text-align: left; }
        </style>
      </head>
      <body>
        <table>
          <!-- Título Principal -->
          <tr>
            <td colspan="8" class="titulo">Reporte de Mantenimientos - MADERERÍA LA HUERTA</td>
          </tr>
          <!-- Fecha de Generación -->
          <tr>
            <td colspan="8" class="fecha">Fecha de generación: ${fechaActual}</td>
          </tr>
          <!-- Espacio -->
          <tr><td colspan="8">&nbsp;</td></tr>
          
          <!-- RESUMEN GENERAL -->
          <tr>
            <td colspan="8" class="header-morado">RESUMEN GENERAL</td>
          </tr>
          <tr>
            <td colspan="2" class="header-azul">Total Mantenimientos</td>
            <td colspan="2" class="header-azul">Monto Total</td>
            <td colspan="2" class="header-azul">Preventivos</td>
            <td colspan="1" class="header-azul">Correctivos</td>
            <td colspan="1" class="header-azul">Emergencia</td>
          </tr>
          <tr>
            <td colspan="2" class="dato">${estadisticas.totalMantenimientos}</td>
            <td colspan="2" class="dato">${formatCurrency(estadisticas.montoTotal)}</td>
            <td colspan="2" class="dato">${estadisticas.mantenimientosPreventivos}</td>
            <td colspan="1" class="dato">${estadisticas.mantenimientosCorrectivos}</td>
            <td colspan="1" class="dato">${estadisticas.mantenimientosEmergencia}</td>
          </tr>
          <tr><td colspan="8">&nbsp;</td></tr>
          
          <!-- DETALLE DE MANTENIMIENTOS -->
          <tr>
            <td colspan="8" class="header-morado">DETALLE DE MANTENIMIENTOS</td>
          </tr>
          <tr>
            <th class="header-morado">Fecha</th>
            <th class="header-morado">Vehículo</th>
            <th class="header-morado">Tipo</th>
            <th class="header-morado">Descripción</th>
            <th class="header-morado">Taller</th>
            <th class="header-morado">Kilometraje</th>
            <th class="header-morado">N° Factura</th>
            <th class="header-morado">Monto</th>
          </tr>`;
    
    // Agregar datos de mantenimientos
    mantenimientosFiltrados.forEach((mantenimiento, index) => {
      const rowClass = index % 2 === 0 ? 'dato' : 'dato-alternado';
      const tipoTexto = mantenimiento.tipoMantenimiento === 'preventivo' ? 'Preventivo' :
                       mantenimiento.tipoMantenimiento === 'correctivo' ? 'Correctivo' :
                       mantenimiento.tipoMantenimiento === 'emergencia' ? 'Emergencia' : 
                       mantenimiento.tipoMantenimiento || '-';
      
      htmlContent += `
          <tr>
            <td class="${rowClass}">${formatDate(mantenimiento.fecha)}</td>
            <td class="${rowClass} text-left">${getAutoNombre(mantenimiento.autoId)}</td>
            <td class="${rowClass}">${tipoTexto}</td>
            <td class="${rowClass} text-left">${mantenimiento.descripcion || '-'}</td>
            <td class="${rowClass} text-left">${mantenimiento.taller || '-'}</td>
            <td class="${rowClass}">${mantenimiento.kilometraje || '-'}</td>
            <td class="${rowClass}">${mantenimiento.nFactura || '-'}</td>
            <td class="${rowClass}">${formatCurrency(mantenimiento.monto)}</td>
          </tr>`;
    });
    
    // Fila de total
    htmlContent += `
          <tr>
            <td colspan="7" class="total-azul">TOTAL GENERAL:</td>
            <td class="total-azul">${formatCurrency(estadisticas.montoTotal)}</td>
          </tr>
        </table>
      </body>
      </html>`;
    
    // Crear blob y descargar
    const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reporte_mantenimientos_${new Date().getTime()}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const estadisticas = calcularEstadisticas();

  return (
    <div className="reporte-mantenimiento">
      <div className="reporte-header">
        <div className="header-title">
          <FiFileText className="header-icon" />
          <h2>Reporte de Mantenimientos</h2>
        </div>
        <button className="btn-exportar" onClick={exportarAExcel}>
          <FiDownload />
          <span>Exportar a Excel</span>
        </button>
      </div>

      {/* Estadísticas Generales */}
      <div className="estadisticas-grid">
        <div className="stat-card stat-card-total">
          <div className="stat-icon">
            <FiTool />
          </div>
          <div className="stat-content">
            <div className="stat-number">{estadisticas.totalMantenimientos}</div>
            <div className="stat-label">Total Mantenimientos</div>
          </div>
        </div>
        <div className="stat-card stat-card-monto">
          <div className="stat-icon">
            <FiDollarSign />
          </div>
          <div className="stat-content">
            <div className="stat-number">{formatCurrency(estadisticas.montoTotal)}</div>
            <div className="stat-label">Monto Total</div>
          </div>
        </div>
        <div className="stat-card stat-card-preventivo">
          <div className="stat-icon">
            <FiCheckCircle />
          </div>
          <div className="stat-content">
            <div className="stat-number">{estadisticas.mantenimientosPreventivos}</div>
            <div className="stat-label">Preventivos</div>
          </div>
        </div>
        <div className="stat-card stat-card-correctivo">
          <div className="stat-icon">
            <FiAlertTriangle />
          </div>
          <div className="stat-content">
            <div className="stat-number">{estadisticas.mantenimientosCorrectivos}</div>
            <div className="stat-label">Correctivos</div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filtros-section">
        <h3>Filtros</h3>
        <div className="filtros-grid">
          <div className="filtro-group">
            <label>Vehículo:</label>
            <select
              value={filtros.autoId}
              onChange={(e) => setFiltros({...filtros, autoId: e.target.value})}
            >
              <option value="">Todas las unidades de reparto</option>
              {autos
                .filter(auto => auto.areaVehicular === 'reparto')
                .map(auto => (
                  <option key={auto.id} value={auto.id}>
                    {getAutoNombre(auto.id)}
                  </option>
                ))}
            </select>
          </div>

          <div className="filtro-group">
            <label>Tipo:</label>
            <select
              value={filtros.tipoMantenimiento}
              onChange={(e) => setFiltros({...filtros, tipoMantenimiento: e.target.value})}
            >
              <option value="">Todos los tipos</option>
              <option value="preventivo">Preventivo</option>
              <option value="correctivo">Correctivo</option>
              <option value="emergencia">Emergencia</option>
            </select>
          </div>

          <div className="filtro-group">
            <label>Fecha Inicio:</label>
            <input
              type="date"
              value={filtros.fechaInicio}
              onChange={(e) => setFiltros({...filtros, fechaInicio: e.target.value})}
            />
          </div>

          <div className="filtro-group">
            <label>Fecha Fin:</label>
            <input
              type="date"
              value={filtros.fechaFin}
              onChange={(e) => setFiltros({...filtros, fechaFin: e.target.value})}
            />
          </div>

          <button 
            className="btn-limpiar"
            onClick={() => setFiltros({
              autoId: '',
              fechaInicio: '',
              fechaFin: '',
              tipoMantenimiento: ''
            })}
          >
            Limpiar Filtros
          </button>
        </div>
      </div>

      {/* Tabla de Mantenimientos */}
      <div className="tabla-section">
        <h3>Registros de Mantenimientos</h3>
        {mantenimientosFiltrados.length === 0 ? (
          <div className="no-data">
            <p>No hay mantenimientos registrados con los filtros seleccionados</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="tabla-mantenimientos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Vehículo</th>
                  <th>Tipo</th>
                  <th>Descripción</th>
                  <th>Taller</th>
                  <th>Kilometraje</th>
                  <th>N° Factura</th>
                  <th>Monto</th>
                </tr>
              </thead>
              <tbody>
                {mantenimientosFiltrados.map((mantenimiento, index) => (
                  <tr key={mantenimiento.id || index}>
                    <td>{formatDate(mantenimiento.fecha)}</td>
                    <td>{getAutoNombre(mantenimiento.autoId)}</td>
                    <td>
                      <span className={`badge badge-${mantenimiento.tipoMantenimiento || 'default'}`}>
                        {mantenimiento.tipoMantenimiento === 'preventivo' ? 'Preventivo' :
                         mantenimiento.tipoMantenimiento === 'correctivo' ? 'Correctivo' :
                         mantenimiento.tipoMantenimiento === 'emergencia' ? 'Emergencia' : 
                         mantenimiento.tipoMantenimiento || '-'}
                      </span>
                    </td>
                    <td className="text-left">{mantenimiento.descripcion || '-'}</td>
                    <td>{mantenimiento.taller || '-'}</td>
                    <td>{mantenimiento.kilometraje ? `${mantenimiento.kilometraje} km` : '-'}</td>
                    <td>{mantenimiento.nFactura || '-'}</td>
                    <td className="text-right">{formatCurrency(mantenimiento.monto)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan="7"><strong>TOTAL:</strong></td>
                  <td className="text-right"><strong>{formatCurrency(estadisticas.montoTotal)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReporteMantenimiento;

